import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ImportJobListItem,
  type ImportJobListQuery,
  type ImportRowListQuery,
} from '@utazzo/shared';
import { toast } from 'sonner';

import * as api from './imports.api';

const QK = {
  modules: ['imports', 'modules'] as const,
  module: (moduleKey: string) => ['imports', 'modules', moduleKey] as const,
  jobsList: (q: ImportJobListQuery) => ['imports', 'jobs', q] as const,
  job: (id: string) => ['imports', 'jobs', id] as const,
  jobRows: (id: string, q: ImportRowListQuery) => ['imports', 'jobs', id, 'rows', q] as const,
};

/** Bust every lead count surface after a successful import commit. */
export const invalidateLeadsAfterImport = (qc: ReturnType<typeof useQueryClient>): void => {
  qc.invalidateQueries({ queryKey: ['leads', 'list'] });
  qc.invalidateQueries({ queryKey: ['leads', 'pipeline-stats'] });
};

export const useImportModules = () =>
  useQuery({ queryKey: QK.modules, queryFn: () => api.listModules(), staleTime: 60_000 });

/**
 * Long-poll the job during VALIDATING / COMMITTING so the wizard can show
 * live status. TanStack Query gives us refetchInterval as a function of the
 * latest data, so we stop polling automatically once the job settles.
 */
export const useImportJob = (jobId: string | null) =>
  useQuery({
    queryKey: jobId ? QK.job(jobId) : ['imports', 'jobs', 'noop'],
    queryFn: () => api.getJob(jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return 1500;
      if (data.status === 'VALIDATING' || data.status === 'COMMITTING') return 1500;
      return false;
    },
  });

export const useImportJobs = (query: ImportJobListQuery) =>
  useQuery({
    queryKey: QK.jobsList(query),
    queryFn: () => api.listJobs(query),
    staleTime: 5_000,
  });

export const useImportRows = (jobId: string | null, query: ImportRowListQuery) =>
  useQuery({
    queryKey: jobId ? QK.jobRows(jobId, query) : ['imports', 'jobs', 'noop', 'rows'],
    queryFn: () => api.listRows(jobId as string, query),
    enabled: Boolean(jobId),
    placeholderData: (prev) => prev,
    // Import rows only change via the operator's own mutations on this
    // page. A 30 s freshness window avoids the default
    // refetch-on-window-focus behaviour which was hammering the API
    // every time the operator alt-tabbed. Mutations still invalidate
    // the prefix so the operator's own edits are still seen
    // immediately.
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

export const useUploadImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleKey, file }: { moduleKey: string; file: File }) =>
      api.uploadImport(moduleKey, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports', 'jobs'] });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Upload failed'),
  });
};

export const usePatchImportRow = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sheetKey = '',
      rowIndex,
      fixedCells,
    }: {
      sheetKey?: string;
      rowIndex: number;
      fixedCells: Record<string, unknown>;
    }) => api.patchRow(jobId, sheetKey, rowIndex, fixedCells),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId] });
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId, 'rows'] });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not save fix'),
  });
};

export const useSkipImportRow = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sheetKey = '',
      rowIndex,
      skip,
    }: {
      sheetKey?: string;
      rowIndex: number;
      skip: boolean;
    }) => api.skipRow(jobId, sheetKey, rowIndex, skip),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId] });
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not update row'),
  });
};

export const useCommitImport = (jobId: string, _opts?: { invalidateOnSuccess?: string[] }) => {
  void _opts;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitJob(jobId),
    // Optimistically flip the cached job to COMMITTING the instant the
    // operator clicks Commit. This makes the progress bar appear right away
    // AND kicks the job poll into gear (its refetchInterval only ticks while
    // status is COMMITTING) - otherwise the bar stayed hidden until the
    // request resolved / the page was refreshed, especially on the inline
    // commit path where the request blocks for the whole run.
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: QK.job(jobId) });
      const prev = qc.getQueryData<ImportJobListItem>(QK.job(jobId));
      if (prev) {
        qc.setQueryData<ImportJobListItem>(QK.job(jobId), { ...prev, status: 'COMMITTING' });
      }
      return { prev };
    },
    onSuccess: (job) => {
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId] });
      if (job.status === 'COMMITTING') {
        toast.info('Import queued - finishing in the background…');
        return;
      }
      invalidateLeadsAfterImport(qc);
      if (job.committedRows > 0) {
        toast.success(
          `Committed ${job.committedRows} row(s).${job.failedRows ? ` ${job.failedRows} failed.` : ''}`,
        );
      } else if (job.failedRows > 0) {
        toast.error(`${job.failedRows} row(s) failed to import. Filter by Failed to see why.`);
      } else {
        toast.error('No rows were imported.');
      }
    },
    onError: (err: unknown, _vars, ctx) => {
      // Roll the optimistic COMMITTING back so a failed commit returns the
      // job to its previous (READY / PARTIAL) state and the Commit button
      // re-enables.
      if (ctx?.prev) qc.setQueryData(QK.job(jobId), ctx.prev);
      toast.error(extractMessage(err) ?? 'Commit failed');
    },
  });
};

/**
 * One-click retry for the rows that failed to commit. Mirrors
 * `useCommitImport`'s optimistic COMMITTING flip so the progress bar appears
 * immediately and the job poll kicks in - the server resets COMMIT_FAILED rows
 * to VALID then re-commits them.
 */
export const useRetryFailedImport = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.retryFailedRows(jobId),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: QK.job(jobId) });
      const prev = qc.getQueryData<ImportJobListItem>(QK.job(jobId));
      if (prev) {
        qc.setQueryData<ImportJobListItem>(QK.job(jobId), { ...prev, status: 'COMMITTING' });
      }
      return { prev };
    },
    onSuccess: (job) => {
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId] });
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId, 'rows'] });
      if (job.status === 'COMMITTING') {
        toast.info('Retry queued - finishing in the background…');
        return;
      }
      invalidateLeadsAfterImport(qc);
      if (job.committedRows > 0) {
        toast.success(
          `Committed ${job.committedRows} row(s).${job.failedRows ? ` ${job.failedRows} still failed.` : ''}`,
        );
      } else if (job.failedRows > 0) {
        toast.error(`${job.failedRows} row(s) still failed. Filter by Failed to see why.`);
      } else {
        toast.error('No rows were retried.');
      }
    },
    onError: (err: unknown, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(QK.job(jobId), ctx.prev);
      toast.error(extractMessage(err) ?? 'Retry failed');
    },
  });
};

/**
 * Re-run validation over an already-uploaded job, without a re-upload - so a
 * row that only failed because a master entry or a validation rule has since
 * changed flips to valid in place. Refreshes the job counters and the row list.
 */
export const useRevalidateImport = (jobId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.revalidateJob(jobId),
    onSuccess: (job) => {
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId] });
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId, 'rows'] });
      if (job.invalidRows === 0) {
        toast.success('Re-validated - every row is valid now.');
      } else {
        toast.info(`Re-validated - ${job.validRows} valid, ${job.invalidRows} still invalid.`);
      }
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Could not re-validate'),
  });
};

/**
 * Short-lived presigned URL to download the original uploaded XLSX. Lazy:
 * the query only fires once the operator clicks "Download source" so we
 * don't waste a signed URL per page render. 4-minute staleTime keeps the
 * URL fresh enough for the typical "click to download" pattern.
 */
export const useImportSourceFile = (jobId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: jobId ? ['imports', 'jobs', jobId, 'source-file'] : ['imports', 'jobs', 'noop'],
    queryFn: () => api.getSourceFile(jobId as string),
    enabled: Boolean(jobId) && enabled,
    staleTime: 4 * 60_000,
    refetchOnWindowFocus: false,
  });

/**
 * Trigger a browser download of the importer's starter XLSX. Used by the
 * "Download sample template" button on every upload page. The mutation
 * surfaces in-flight state for a loading spinner; success / error toast
 * the operator hears about.
 */
export const useDownloadSampleTemplate = () =>
  useMutation({
    mutationFn: (moduleKey: string) => api.downloadSampleTemplate(moduleKey),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Could not download sample template'),
  });

/**
 * Trigger a browser download of the importer's per-entity operator
 * guide (Markdown). Mirrors `useDownloadSampleTemplate` for the docs
 * payload so the upload page can offer a "Download guide" button next
 * to "Download sample template".
 */
export const useDownloadOperatorGuide = () =>
  useMutation({
    mutationFn: (moduleKey: string) => api.downloadOperatorGuide(moduleKey),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Could not download operator guide'),
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
